import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CheckCircle2, Upload, X, Calendar, Phone, Mail, Globe, 
  MapPin, FileText, Image, AlertCircle, Loader2, ChevronDown,
  ChevronUp, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';

const API_BASE = `${window.location.origin}/api`;

export default function OnboardingPublic() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const signatureRefs = useRef({});

  // Fetch form by slug
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await fetch(`${API_BASE}/onboarding/forms/slug/${slug}`);
        if (!res.ok) {
          throw new Error('Form not found');
        }
        const data = await res.json();
        setForm(data);
        
        // Initialize expanded sections (all expanded by default)
        const expanded = {};
        data.sections?.forEach(section => {
          expanded[section.id] = true;
        });
        setExpandedSections(expanded);
        
        // Initialize date fields with today's date
        const initialAnswers = {};
        data.sections?.forEach(section => {
          section.fields?.forEach(field => {
            if (field.type === 'date') {
              initialAnswers[field.id] = new Date().toISOString().split('T')[0];
            }
          });
        });
        setAnswers(initialAnswers);
      } catch (err) {
        console.error('Failed to fetch form:', err);
        toast.error('Form not found or not available');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchForm();
    }
  }, [slug]);

  // Handle input change
  const handleInputChange = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_BASE}/onboarding/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.ok) {
        setFiles(prev => ({ ...prev, [fieldId]: data.data }));
        setAnswers(prev => ({ ...prev, [fieldId]: file.name }));
        toast.success(`File uploaded: ${file.name}`);
      }
    } catch (err) {
      toast.error('Failed to upload file');
    }
  };

  // Handle signature
  const handleSignature = (fieldId) => {
    const sigCanvas = signatureRefs.current[fieldId];
    if (sigCanvas && !sigCanvas.isEmpty()) {
      const dataUrl = sigCanvas.toDataURL();
      setAnswers(prev => ({ ...prev, [fieldId]: dataUrl }));
    }
  };

  // Clear signature
  const clearSignature = (fieldId) => {
    const sigCanvas = signatureRefs.current[fieldId];
    if (sigCanvas) {
      sigCanvas.clear();
      setAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[fieldId];
        return newAnswers;
      });
    }
  };

  // Toggle section
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    form.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.required && !answers[field.id]) {
          newErrors[field.id] = 'This field is required';
        }
        if (field.type === 'email' && answers[field.id]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(answers[field.id])) {
            newErrors[field.id] = 'Please enter a valid email';
          }
        }
      });
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_BASE}/onboarding/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_id: form.id,
          answers,
          files,
        }),
      });
      
      const data = await res.json();
      if (data.ok) {
        setSubmitted(true);
        toast.success('Form submitted successfully!');
      } else {
        throw new Error(data.detail || 'Failed to submit');
      }
    } catch (err) {
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render field based on type
  const renderField = (field) => {
    const hasError = errors[field.id];
    const baseInputClass = `bg-white/5 border ${hasError ? 'border-red-500' : 'border-white/20'} text-white placeholder:text-gray-500 focus:border-white/50`;
    
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={answers[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            maxLength={field.character_limit}
            className={baseInputClass}
          />
        );
        
      case 'paragraph':
        return (
          <textarea
            value={answers[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            maxLength={field.character_limit}
            rows={5}
            className={`w-full rounded-lg px-4 py-3 ${baseInputClass}`}
          />
        );
        
      case 'email':
        return (
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="email"
              value={answers[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder || 'email@example.com'}
              className={`pl-10 ${baseInputClass}`}
            />
          </div>
        );
        
      case 'phone':
        return (
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="tel"
              value={answers[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder || '+1 (555) 000-0000'}
              className={`pl-10 ${baseInputClass}`}
            />
          </div>
        );
        
      case 'url':
      case 'website':
        return (
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="url"
              value={answers[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder || 'https://example.com'}
              className={`pl-10 ${baseInputClass}`}
            />
          </div>
        );
        
      case 'date':
        return (
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="date"
              value={answers[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`pl-10 ${baseInputClass}`}
            />
          </div>
        );
        
      case 'dropdown':
        return (
          <select
            value={answers[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className={`w-full rounded-lg px-4 py-3 ${baseInputClass}`}
          >
            <option value="">Select an option</option>
            {field.options?.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
        
      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {field.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                <input
                  type="radio"
                  name={field.id}
                  value={opt}
                  checked={answers[field.id] === opt}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="w-4 h-4 text-white"
                />
                <span className="text-white">{opt}</span>
              </label>
            ))}
          </div>
        );
        
      case 'checkboxes':
        return (
          <div className="space-y-2">
            {field.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                <input
                  type="checkbox"
                  value={opt}
                  checked={(answers[field.id] || []).includes(opt)}
                  onChange={(e) => {
                    const current = answers[field.id] || [];
                    if (e.target.checked) {
                      handleInputChange(field.id, [...current, opt]);
                    } else {
                      handleInputChange(field.id, current.filter(v => v !== opt));
                    }
                  }}
                  className="w-4 h-4"
                />
                <span className="text-white">{opt}</span>
              </label>
            ))}
          </div>
        );
        
      case 'file_upload':
      case 'image_upload':
      case 'pdf_upload':
        return (
          <div className={`border-2 border-dashed ${hasError ? 'border-red-500' : 'border-white/20'} rounded-xl p-6 text-center`}>
            {files[field.id] ? (
              <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-400" />
                  <span className="text-white text-sm">{answers[field.id]}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFiles(prev => {
                      const newFiles = { ...prev };
                      delete newFiles[field.id];
                      return newFiles;
                    });
                    setAnswers(prev => {
                      const newAnswers = { ...prev };
                      delete newAnswers[field.id];
                      return newAnswers;
                    });
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-white mb-1">Drop files here or click to upload</p>
                <p className="text-sm text-gray-400">{field.help_text || 'Accepted formats: PDF, DOCX, XLSX, Images'}</p>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(field.id, e.target.files[0])}
                  accept={field.accept?.join(',') || '*'}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </>
            )}
          </div>
        );
        
      case 'address':
        return (
          <div className="space-y-3">
            <Input
              value={answers[`${field.id}_street`] || ''}
              onChange={(e) => handleInputChange(`${field.id}_street`, e.target.value)}
              placeholder="Street Address"
              className={baseInputClass}
            />
            <Input
              value={answers[`${field.id}_suite`] || ''}
              onChange={(e) => handleInputChange(`${field.id}_suite`, e.target.value)}
              placeholder="Suite / Unit (optional)"
              className={baseInputClass}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={answers[`${field.id}_city`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_city`, e.target.value)}
                placeholder="City"
                className={baseInputClass}
              />
              <Input
                value={answers[`${field.id}_state`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_state`, e.target.value)}
                placeholder="State"
                className={baseInputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={answers[`${field.id}_zip`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_zip`, e.target.value)}
                placeholder="ZIP Code"
                className={baseInputClass}
              />
              <Input
                value={answers[`${field.id}_country`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_country`, e.target.value)}
                placeholder="Country"
                className={baseInputClass}
              />
            </div>
          </div>
        );
        
      case 'signature':
        return (
          <div className={`border ${hasError ? 'border-red-500' : 'border-white/20'} rounded-xl overflow-hidden`}>
            <div className="bg-white">
              <SignatureCanvas
                ref={(ref) => signatureRefs.current[field.id] = ref}
                penColor="black"
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'w-full',
                }}
                onEnd={() => handleSignature(field.id)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5">
              <p className="text-sm text-gray-400">Sign above</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => clearSignature(field.id)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Clear
              </Button>
            </div>
          </div>
        );
        
      case 'section_divider':
        return <hr className="border-white/20" />;
        
      case 'notes':
        return (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-yellow-400 text-sm">{field.description || field.label}</p>
          </div>
        );
        
      case 'repeatable':
        return <RepeatableField field={field} answers={answers} onChange={handleInputChange} />;
        
      default:
        return (
          <Input
            value={answers[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className={baseInputClass}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Form Not Found</h1>
          <p className="text-gray-400">This onboarding form is not available or has been removed.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-gray-400 mb-2">Your onboarding form has been submitted successfully.</p>
          <p className="text-gray-400">Our team will review your information and reach out shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="onboarding-public">
      {/* Cover Image */}
      {form.cover_image_url && (
        <div className="h-48 md:h-64 bg-gradient-to-b from-white/10 to-transparent">
          <img
            src={form.cover_image_url}
            alt="Cover"
            className="w-full h-full object-cover opacity-50"
          />
        </div>
      )}

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-10">
          {form.logo_url && (
            <img
              src={form.logo_url}
              alt="Logo"
              className="h-16 md:h-20 mx-auto mb-6"
            />
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{form.name}</h1>
          {form.description && (
            <p className="text-gray-400 max-w-lg mx-auto">{form.description}</p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {form.sections?.map((section, sectionIndex) => (
            <div
              key={section.id}
              className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden"
            >
              {/* Section Header */}
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
              >
                <div>
                  <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                  {section.description && (
                    <p className="text-sm text-gray-400 mt-1">{section.description}</p>
                  )}
                </div>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Section Fields */}
              {expandedSections[section.id] && (
                <div className="px-6 pb-6 space-y-6">
                  {section.fields?.map((field) => (
                    <div key={field.id} className="space-y-2">
                      {field.type !== 'section_divider' && field.type !== 'notes' && (
                        <label className="block">
                          <span className="text-white font-medium">
                            {field.label}
                            {field.required && <span className="text-red-400 ml-1">*</span>}
                          </span>
                          {field.help_text && field.type !== 'file_upload' && (
                            <p className="text-sm text-gray-400 mt-1">{field.help_text}</p>
                          )}
                        </label>
                      )}
                      
                      <div className="relative">
                        {renderField(field)}
                      </div>
                      
                      {errors[field.id] && (
                        <p className="text-sm text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors[field.id]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto md:min-w-[300px] bg-white text-black hover:bg-gray-200 py-6 text-lg font-semibold rounded-xl"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Complete Onboarding
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-10">
          Powered by CREATRVERSE
        </p>
      </div>
    </div>
  );
}

// Repeatable Field Component
function RepeatableField({ field, answers, onChange }) {
  const [items, setItems] = useState([{ id: 1 }]);

  const addItem = () => {
    setItems(prev => [...prev, { id: Date.now() }]);
  };

  const removeItem = (itemId) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={item.id} className="bg-white/5 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Entry {index + 1}</span>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="text-gray-400 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {field.fields?.map((subField) => (
            <div key={subField.id}>
              <label className="text-sm text-gray-400 block mb-1">{subField.label}</label>
              <Input
                value={answers[`${field.id}_${item.id}_${subField.id}`] || ''}
                onChange={(e) => onChange(`${field.id}_${item.id}_${subField.id}`, e.target.value)}
                placeholder={subField.placeholder}
                className="bg-white/5 border-white/20 text-white"
              />
            </div>
          ))}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={addItem}
        className="w-full border-dashed border-white/30 text-white hover:bg-white/5"
      >
        + Add Another
      </Button>
    </div>
  );
}
